if (!customElements.get('sf-quantity-bundles')) {
  class SplitflaskQuantityBundles extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;
      this.initialized = true;
      this.unitPrice = Number(this.dataset.unitPrice || 0);
      this.currency = this.dataset.currency || 'USD';
      this.moneyFormat = this.dataset.moneyFormat || '${{amount}}';
      this.offerVersion = this.dataset.offerVersion || 'volume-discounts';
      this.volumeDiscountCodes = new Map([
        [2, String(this.dataset.twoPackDiscountCode || '').trim()],
        [3, String(this.dataset.threePackDiscountCode || '').trim()],
      ]);
      this.bogoDiscountCodes = new Map([
        [3, String(this.dataset.bogoBuyTwoDiscountCode || '').trim()],
        [5, String(this.dataset.bogoBuyThreeDiscountCode || '').trim()],
      ]);
      this.managedBogoDiscountCodes = Array.from(this.bogoDiscountCodes.values()).filter(Boolean);
      this.managedOfferDiscountCodes = Array.from(new Set([
        ...this.volumeDiscountCodes.values(),
        ...this.bogoDiscountCodes.values(),
      ])).filter(Boolean);
      this.paidBogoVariantIds = new Set(
        String(this.dataset.bogoPaidVariantIds || '')
          .split(',')
          .map((id) => Number(id.trim()))
          .filter(Boolean)
      );
      this.mysteryVariantId = Number(this.dataset.bogoMysteryVariantId || 0);
      this.mysteryUnitPrice = Number(this.dataset.bogoMysteryUnitPrice || this.unitPrice);
      this.productInfo = this.closest('product-info');
      this.productForm = this.productInfo?.querySelector('product-form');
      this.form = this.productForm?.querySelector('form');
      this.cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
      this.variants = this.getVariants();
      this.primaryVariantId = Number(this.form?.querySelector('[name="id"]')?.value || this.variants[0]?.id || 0);
      this.maxQuantity = Number(this.dataset.maxQuantity) || Math.max(
        1,
        ...Array.from(this.querySelectorAll('[data-sf-bundle-tier]')).map((tier) => Number(tier.dataset.quantity || 1))
      );
      this.currentQuantity = 1;
      this.error = false;

      this.addEventListener('change', this.onChange);
      this.form?.addEventListener('submit', this.onFormSubmit, true);
      this.variantChangeUnsubscriber = typeof subscribe === 'function'
        ? subscribe(PUB_SUB_EVENTS.variantChange, this.onVariantChange)
        : null;

      this.syncColorSelectsToPrimary();
      this.updatePrices();
      this.applySelection(this.querySelector('[data-sf-bundle-choice]:checked')?.value || 1);
    }

    disconnectedCallback() {
      this.removeEventListener('change', this.onChange);
      this.form?.removeEventListener('submit', this.onFormSubmit, true);
      if (this.variantChangeUnsubscriber) this.variantChangeUnsubscriber();
      this.initialized = false;
    }

    getVariants() {
      try {
        const variantJson = this.productInfo?.querySelector('[data-all-variants]')?.textContent;
        return variantJson ? JSON.parse(variantJson) : [];
      } catch (error) {
        console.warn('Splitflask bundles: variant data could not be read.', error);
        return [];
      }
    }

    onChange = (event) => {
      const choice = event.target.closest('[data-sf-bundle-choice]');
      if (choice) {
        this.applySelection(choice.value);
        return;
      }

      const colorSelect = event.target.closest('[data-sf-color-select]');
      if (!colorSelect) return;

      const slotIndex = Number(colorSelect.dataset.slotIndex || 1);
      if (slotIndex > 1) colorSelect.dataset.userSelected = 'true';
      this.updateColorSwatch(colorSelect);

      if (slotIndex === 1) {
        const sourceVariantId = Number(colorSelect.selectedOptions[0]?.dataset.sourceVariantId || colorSelect.value);
        this.selectPrimaryVariant(sourceVariantId);
      } else {
        this.applySelection(this.currentQuantity);
      }
    };

    onVariantChange = (event) => {
      const detail = event?.data;
      if (!detail || String(detail.sectionId) !== String(this.dataset.sectionId) || !detail.variant) return;

      this.primaryVariantId = Number(detail.variant.id);
      if (detail.variant.price != null) {
        this.unitPrice = Number(detail.variant.price);
        this.dataset.unitPrice = String(detail.variant.price);
      }

      this.syncColorSelectsToPrimary();
      this.updatePrices();
      this.applySelection(this.currentQuantity);
    };

    calculate(quantity, discount, paidQuantity = null, targetTotal = null) {
      const regularTotal = this.unitPrice * quantity;
      const hasPaidQuantity = Number.isFinite(paidQuantity) && paidQuantity > 0;
      const hasTargetTotal = Number.isFinite(targetTotal) && targetTotal > 0;
      const total = hasTargetTotal
        ? targetTotal
        : hasPaidQuantity
        ? this.unitPrice * Math.min(quantity, paidQuantity)
        : Math.round(regularTotal * (100 - discount) / 100);
      return {
        regularTotal,
        total,
        perItem: Math.round(total / quantity),
        savings: regularTotal - total,
      };
    }

    calculateSelectedBundle(quantity, discount, paidQuantity = null, targetTotal = null) {
      const selections = this.getSlotSelections(quantity);
      const selectedPrices = selections.map((selection) => selection.price);
      const regularTotal = selectedPrices.reduce((sum, price) => sum + price, 0) || this.unitPrice * quantity;
      const hasPaidQuantity = Number.isFinite(paidQuantity) && paidQuantity > 0;
      const freeQuantity = hasPaidQuantity ? Math.max(0, quantity - paidQuantity) : 0;
      const mysterySelections = selections.filter((selection) => selection.isMystery);
      const freeTotal = mysterySelections.length === freeQuantity
        ? mysterySelections.reduce((sum, selection) => sum + selection.price, 0)
        : [...selectedPrices]
          .sort((priceA, priceB) => priceA - priceB)
          .slice(0, freeQuantity)
          .reduce((sum, price) => sum + price, 0);
      const hasTargetTotal = Number.isFinite(targetTotal) && targetTotal > 0;
      const total = hasTargetTotal
        ? targetTotal
        : hasPaidQuantity
        ? regularTotal - freeTotal
        : Math.round(regularTotal * (100 - discount) / 100);

      return {
        regularTotal,
        total,
        perItem: Math.round(total / quantity),
        savings: regularTotal - total,
      };
    }

    formatMoney(cents) {
      if (typeof formatPrice === 'function') return formatPrice(cents, this.moneyFormat);
      return new Intl.NumberFormat(document.documentElement.lang || 'en-US', {
        style: 'currency',
        currency: this.currency,
      }).format(cents / 100);
    }

    updatePrices() {
      this.querySelectorAll('[data-sf-bundle-tier]').forEach((tier) => {
        const quantity = Number(tier.dataset.quantity || 1);
        const discount = Number(tier.dataset.discount || 0);
        const paidQuantity = tier.dataset.paidQuantity == null ? null : Number(tier.dataset.paidQuantity);
        const targetTotal = Number(tier.dataset.targetTotal || 0) || null;
        const prices = this.calculate(quantity, discount, paidQuantity, targetTotal);
        const total = tier.querySelector('[data-sf-bundle-total]');
        const perItem = tier.querySelector('[data-sf-bundle-per-item]');
        const compare = tier.querySelector('[data-sf-bundle-compare]');
        const savings = tier.querySelector('[data-sf-bundle-savings]');
        const savingsAmount = tier.querySelector('[data-sf-bundle-savings-amount]');

        if (total) total.textContent = this.formatMoney(prices.total);
        if (perItem) perItem.textContent = `${this.formatMoney(prices.perItem)} / ea`;
        if (compare) compare.textContent = this.formatMoney(prices.regularTotal);
        if (savingsAmount) {
          savingsAmount.textContent = this.formatMoney(prices.savings);
        } else if (savings) {
          savings.textContent = `Save ${this.formatMoney(prices.savings)}`;
        }
      });
    }

    applySelection(value) {
      const quantity = Math.min(this.maxQuantity, Math.max(1, Number(value || 1)));
      const selectedTier = this.querySelector(`[data-sf-bundle-tier][data-quantity="${quantity}"]`);
      const discount = Number(selectedTier?.dataset.discount || 0);
      const paidQuantity = selectedTier?.dataset.paidQuantity == null
        ? null
        : Number(selectedTier.dataset.paidQuantity);
      const targetTotal = Number(selectedTier?.dataset.targetTotal || 0) || null;
      const selectedChoice = this.querySelector(`[data-sf-bundle-choice][value="${quantity}"]`);
      if (selectedChoice && !selectedChoice.checked) selectedChoice.checked = true;
      this.currentQuantity = quantity;

      this.updateColorPanel(quantity, paidQuantity);
      const prices = this.calculateSelectedBundle(quantity, discount, paidQuantity, targetTotal);
      this.currentDiscount = prices.regularTotal > 0
        ? prices.savings / prices.regularTotal * 100
        : 0;
      this.currentSavings = prices.savings;
      this.currentPaidQuantity = paidQuantity;
      this.currentTotal = prices.total;
      this.updateSubmitLabels(quantity, prices.total);
      this.updateDynamicCheckout(quantity);

      this.dispatchEvent(new CustomEvent('sf:bundle-change', {
        bubbles: true,
        detail: {
          quantity,
          discount: this.currentDiscount,
          paidQuantity,
          total: prices.total,
          savings: prices.savings,
        },
      }));
    }

    updateColorPanel(quantity, paidQuantity = null) {
      const colorPanel = this.querySelector('[data-sf-bundle-colors]');
      if (colorPanel) colorPanel.hidden = quantity === 1;

      this.querySelectorAll('[data-sf-color-slot]').forEach((slot) => {
        const slotIndex = Number(slot.dataset.slotIndex || 1);
        const slotHidden = quantity === 1 || slotIndex > quantity;
        const isMystery = this.offerVersion === 'buy-get-free'
          && Number.isFinite(paidQuantity)
          && slotIndex > paidQuantity
          && !slotHidden;
        slot.hidden = slotHidden;
        slot.classList.toggle('sf-bundle-color--mystery', isMystery);

        const select = slot.querySelector('[data-sf-color-select]');
        const paidControl = slot.querySelector('[data-sf-paid-color-control]');
        const mysteryControl = slot.querySelector('[data-sf-mystery-color]');
        const mysteryInput = slot.querySelector('[data-sf-mystery-item]');

        if (paidControl) paidControl.hidden = isMystery;
        if (mysteryControl) mysteryControl.hidden = !isMystery;
        if (mysteryInput) mysteryInput.disabled = !isMystery;

        if (select) {
          select.disabled = slotHidden || isMystery;
          if (!isMystery && (slotIndex === 1 || !select.dataset.userSelected)) {
            this.setSelectVariant(select, this.primaryVariantId);
          }
          this.updateColorSwatch(select);
        }
      });

      const checkoutNote = this.querySelector('[data-sf-bundle-checkout-note]');
      if (checkoutNote) checkoutNote.hidden = quantity === 1;
    }

    syncColorSelectsToPrimary() {
      this.querySelectorAll('[data-sf-color-select]').forEach((select) => {
        const slotIndex = Number(select.dataset.slotIndex || 1);
        if (slotIndex === 1 || !select.dataset.userSelected) {
          this.setSelectVariant(select, this.primaryVariantId);
        }
        this.updateColorSwatch(select);
      });
    }

    setSelectVariant(select, variantId) {
      const option = Array.from(select.options).find(
        (item) => (
          Number(item.value) === Number(variantId)
          || Number(item.dataset.sourceVariantId) === Number(variantId)
        ) && !item.disabled
      );
      if (option) select.value = option.value;
    }

    updateColorSwatch(select) {
      const swatch = select.closest('[data-sf-color-slot]')?.querySelector('[data-sf-color-swatch]');
      if (!swatch) return;
      const swatchValue = select.selectedOptions[0]?.dataset.swatch || '';
      swatch.style.setProperty('--sf-bundle-swatch', swatchValue || 'transparent');
    }

    selectPrimaryVariant(variantId) {
      const variant = this.variants.find((item) => Number(item.id) === Number(variantId));
      const variantSelectors = this.productInfo?.querySelector('product-variant-selects');
      if (!variant || !variantSelectors) return;

      const optionGroups = Array.from(variantSelectors.querySelectorAll('.product-form__input'));
      let finalControl = null;

      variant.options.forEach((optionValue, index) => {
        const group = optionGroups[index];
        if (!group) return;

        const select = group.querySelector('select');
        if (select) {
          const matchingOption = Array.from(select.options).find((option) => option.value === optionValue && !option.disabled);
          if (matchingOption) {
            select.value = matchingOption.value;
            finalControl = select;
          }
          return;
        }

        const radio = Array.from(group.querySelectorAll('input[type="radio"]')).find(
          (input) => input.value === optionValue && !input.disabled
        );
        if (radio) {
          radio.checked = true;
          finalControl = radio;
        }
      });

      if (finalControl) finalControl.dispatchEvent(new Event('change', { bubbles: true }));
    }

    updateSubmitLabels(quantity, total) {
      const submitButton = this.productInfo?.querySelector('[id^="ProductSubmitButton-"]');
      const submitLabel = submitButton?.querySelector(':scope > span');
      if (submitButton && submitLabel && !submitButton.disabled) {
        submitLabel.textContent = quantity === 1
          ? `Add to cart · ${this.formatMoney(total)}`
          : `Add ${quantity} to cart · ${this.formatMoney(total)}`;
      }

      const stickyLabel = this.productInfo?.querySelector('.product-bar__submit span');
      if (stickyLabel) stickyLabel.textContent = quantity === 1 ? 'Add to cart' : `Add ${quantity}`;
    }

    updateDynamicCheckout(quantity) {
      const paymentButton = this.productInfo?.querySelector('.shopify-payment-button');
      if (!paymentButton) return;
      paymentButton.hidden = quantity > 1;
      paymentButton.setAttribute('aria-hidden', quantity > 1 ? 'true' : 'false');
    }

    getOfferDiscountCode(quantity = this.currentQuantity) {
      const codes = this.offerVersion === 'buy-get-free'
        ? this.bogoDiscountCodes
        : this.volumeDiscountCodes;
      return codes.get(Number(quantity)) || '';
    }

    async getCartData() {
      try {
        const cartUrl = routes.cart_url.endsWith('.js') ? routes.cart_url : `${routes.cart_url}.js`;
        const response = await fetch(cartUrl, { headers: { Accept: 'application/json' } });
        if (!response.ok) return null;
        return await response.json();
      } catch (error) {
        return null;
      }
    }

    async updateOfferDiscountCode(fallbackCartData, selectedCode = '') {
      if (!this.managedOfferDiscountCodes.length) return fallbackCartData;

      const existingCartData = await this.getCartData();
      const existingCodes = (existingCartData?.discount_codes || [])
        .map((discount) => typeof discount === 'string' ? discount : discount.code)
        .filter(Boolean);
      const managedCodes = new Set(this.managedOfferDiscountCodes.map((code) => code.toLowerCase()));
      const discountCodes = existingCodes.filter((code) => !managedCodes.has(String(code).toLowerCase()));
      if (selectedCode) discountCodes.push(selectedCode);

      const body = { discount: Array.from(new Set(discountCodes)).join(',') };
      if (this.offerVersion !== 'buy-get-free' && existingCartData?.items) {
        const giftUpdates = existingCartData.items.reduce((updates, item) => {
          const variantId = Number(item.variant_id || item.id);
          if (
            item.key
            && (variantId === this.mysteryVariantId || this.paidBogoVariantIds.has(variantId))
          ) {
            updates[item.key] = 0;
          }
          return updates;
        }, {});
        if (Object.keys(giftUpdates).length) body.updates = giftUpdates;
      }
      if (this.cart?.getSectionsToRender) {
        body.sections = this.cart.getSectionsToRender().map((section) => section.section);
        body.sections_url = window.location.pathname;
      }

      const updateUrl = routes.cart_update_url.endsWith('.js')
        ? routes.cart_update_url
        : `${routes.cart_update_url}.js`;
      const response = await fetch(updateUrl, {
        method: 'POST',
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json',
          'X-Requested-With': 'XMLHttpRequest',
        },
        body: JSON.stringify(body),
      });
      const cartData = await response.json();

      if (!response.ok || cartData.status || cartData.errors) {
        throw new Error(cartData.description || cartData.message || 'The bundle discount could not be updated.');
      }

      if (selectedCode) {
        const appliedDiscount = (cartData.discount_codes || []).find(
          (discount) => String(discount.code || discount).toLowerCase() === selectedCode.toLowerCase()
        );
        if (!appliedDiscount || appliedDiscount.applicable === false) {
          throw new Error(`The discount code ${selectedCode} is missing or not applicable.`);
        }
      }

      return cartData;
    }

    getSlotSelections(quantity) {
      return Array.from(this.querySelectorAll('[data-sf-color-slot]'))
        .filter((slot) => Number(slot.dataset.slotIndex || 1) <= quantity && !slot.hidden)
        .map((slot) => {
          const mysteryInput = slot.querySelector('[data-sf-mystery-item]');
          if (mysteryInput && !mysteryInput.disabled) {
            return {
              id: Number(mysteryInput.value || this.mysteryVariantId),
              price: Number(mysteryInput.dataset.unitPrice || this.mysteryUnitPrice || this.unitPrice),
              isMystery: true,
            };
          }

          const select = slot.querySelector('[data-sf-color-select]');
          if (!select || select.disabled || select.selectedOptions[0]?.disabled) return null;
          const variant = this.variants.find((item) => Number(item.id) === Number(select.value));
          return {
            id: Number(select.value),
            price: Number(select.selectedOptions[0]?.dataset.unitPrice || variant?.price || this.unitPrice),
            isMystery: false,
          };
        })
        .filter((selection) => selection?.id);
    }

    getItems(quantity) {
      const counts = new Map();
      this.getSlotSelections(quantity).forEach((selection) => {
        counts.set(selection.id, (counts.get(selection.id) || 0) + 1);
      });

      return Array.from(counts, ([id, itemQuantity]) => ({ id, quantity: itemQuantity }));
    }

    onFormSubmit = async (event) => {
      if (this.currentQuantity <= 1) return;

      event.preventDefault();
      event.stopImmediatePropagation();
      if (this.submitting) return;

      const offerDiscountCode = this.getOfferDiscountCode();
      if (
        this.offerVersion === 'buy-get-free'
        && (!offerDiscountCode || !this.mysteryVariantId || !this.paidBogoVariantIds.size)
      ) {
        this.productForm?.handleErrorMessage?.('This Buy X, Get Y offer is temporarily unavailable. Please try again shortly.');
        return;
      }
      if (this.offerVersion !== 'buy-get-free' && !offerDiscountCode) {
        this.productForm?.handleErrorMessage?.('This bundle offer is temporarily unavailable. Please try again shortly.');
        return;
      }

      const items = this.getItems(this.currentQuantity);
      const selectedItemCount = items.reduce((sum, item) => sum + item.quantity, 0);
      if (selectedItemCount !== this.currentQuantity) {
        this.productForm?.handleErrorMessage?.('Please choose an available color for every unit.');
        return;
      }

      const accessoryPackage = this.productInfo?.querySelector('sf-accessory-package');
      const packageSelected = accessoryPackage?.packageSelected === true;
      if (packageSelected) {
        const accessoryItem = accessoryPackage.getAccessoryCartItem?.(this.currentQuantity);
        if (!accessoryItem?.id || !accessoryItem.quantity) {
          const message = 'Please choose an available accessory color.';
          if (typeof accessoryPackage.showError === 'function') {
            accessoryPackage.showError(message);
          } else {
            this.productForm?.handleErrorMessage?.(message);
          }
          return;
        }
        items.push(accessoryItem);
      }

      this.submitting = true;
      this.error = false;
      this.activeSubmitter = event.submitter;
      this.productForm?.handleErrorMessage?.();
      this.setLoading(true);
      accessoryPackage?.setExternalLoading?.(true);
      if (packageSelected) accessoryPackage?.updateStatus?.('Adding cups and accessory to cart');

      try {
        const body = { items };
        if (this.cart) {
          body.sections = this.cart.getSectionsToRender().map((section) => section.section);
          body.sections_url = window.location.pathname;
          this.cart.setActiveElement?.(document.activeElement);
        }

        const response = await fetch(routes.cart_add_url, {
          method: 'POST',
          headers: {
            'Accept': 'application/json',
            'Content-Type': 'application/json',
            'X-Requested-With': 'XMLHttpRequest',
          },
          body: JSON.stringify(body),
        });
        let cartData = await response.json();

        if (!response.ok || cartData.status) {
          const message = cartData.description || cartData.message || 'We could not add this bundle. Please try again.';
          this.error = true;
          this.productForm?.handleErrorMessage?.(message);
          if (packageSelected) accessoryPackage?.updateStatus?.(message);
          if (typeof publish === 'function') {
            publish(PUB_SUB_EVENTS.cartError, {
              source: 'splitflask-quantity-bundles',
              productVariantId: this.primaryVariantId,
              errors: cartData.errors || message,
              message,
            });
          }
          return;
        }

        if (this.managedOfferDiscountCodes.length) {
          try {
            cartData = await this.updateOfferDiscountCode(cartData, offerDiscountCode);
          } catch (discountError) {
            this.error = true;
            const message = this.offerVersion === 'buy-get-free'
              ? 'Your cups were added, but the Buy X, Get Y discount could not be applied. Please review your cart before checkout.'
              : 'Your cups were added, but the bundle discount could not be applied. Please review your cart before checkout.';
            this.productForm?.handleErrorMessage?.(message);
            if (packageSelected) accessoryPackage?.updateStatus?.(message);
            if (this.cart) {
              this.cart.renderContents(cartData);
            } else {
              window.location = routes.cart_url;
            }
            console.error(discountError);
            return;
          }
        }

        if (packageSelected && accessoryPackage?.accessoryDiscountCode && accessoryPackage.accessoryDiscount > 0) {
          accessoryPackage?.updateStatus?.(
            accessoryPackage.accessoryDiscount === 100
              ? 'Applying your free handles'
              : `Applying ${accessoryPackage.accessoryDiscount}% handle savings`
          );
          try {
            cartData = await accessoryPackage.applyBundleDiscount?.(cartData) || cartData;
          } catch (discountError) {
            this.error = true;
            const message = 'Cups and handles were added, but the free-handle offer could not be applied. Please review your cart.';
            this.productForm?.handleErrorMessage?.(message);
            accessoryPackage?.updateStatus?.(message);
            if (this.cart) {
              this.cart.renderContents(cartData);
            } else {
              window.location = routes.cart_url;
            }
            console.error(discountError);
            return;
          }
        }

        if (!this.cart) {
          window.location = routes.cart_url;
          return;
        }

        if (typeof publish === 'function') {
          publish(PUB_SUB_EVENTS.cartUpdate, {
            source: 'splitflask-quantity-bundles',
            productVariantId: this.primaryVariantId,
            cartData,
          });
        }
        if (packageSelected) accessoryPackage?.updateStatus?.('Cups and accessory added to cart');
        this.cart.renderContents(cartData);
      } catch (error) {
        this.error = true;
        const message = 'We could not add this bundle. Please check your connection and try again.';
        this.productForm?.handleErrorMessage?.(message);
        if (packageSelected) accessoryPackage?.updateStatus?.(message);
        console.error(error);
      } finally {
        this.setLoading(false);
        accessoryPackage?.setExternalLoading?.(false);
        this.submitting = false;
        this.activeSubmitter = null;
      }
    };

    setLoading(isLoading) {
      const submitButton = this.productInfo?.querySelector('[id^="ProductSubmitButton-"]');
      const spinner = submitButton?.querySelector('.loading__spinner');
      [submitButton, this.activeSubmitter].filter(Boolean).forEach((button) => {
        button.classList.toggle('loading', isLoading);
        if (isLoading) {
          button.setAttribute('aria-disabled', 'true');
        } else if (!this.error) {
          button.removeAttribute('aria-disabled');
        }
      });
      spinner?.classList.toggle('hidden', !isLoading);
    }
  }

  customElements.define('sf-quantity-bundles', SplitflaskQuantityBundles);
}
